import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAPI, useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { Button, SettingsCard } from "@humansignal/ui";
import { cn } from "../../utils/bem";
import { ProjectContext, ProjectProvider } from "../../providers/ProjectProvider";
import { ConsentGate } from "../../components/ConsentGate/ConsentGate";
import { RateChangeGate } from "../../components/RateChangeGate/RateChangeGate";
import { ProjectNav } from "../ProjectDashboard/ProjectNav";
import { TabHeader } from "../../components/TabHeader/TabHeader";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { ProjectLocaleScope } from "../../i18n/ProjectLocaleScope";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./BulkLabel.prefix.css";

const block = cn("bulk-label");

/**
 * Bulk mode — rapid-fire labelling for simple classification
 * (docs/annotation/bulk-mode). One page of tasks at a time; clicking a
 * choice submits that task instantly through the ordinary annotation
 * endpoint (review / agreement / QC pipelines all still apply), the
 * status chip flips, and focus advances. Clicking a different choice
 * afterwards re-saves via PATCH.
 *
 * Only mounts usefully when the project's config passed the
 * ``config_suitable_for_bulk_annotation`` gate: exactly the configs
 * where the whole annotation is one Choices value.
 */

// Resolve the single Choices control out of ``parsed_label_config``:
// control/object names for the result payload, the option list, and
// which data key + media type each task renders.
const resolveChoicesControl = (project) => {
  const parsed = project?.parsed_label_config;
  if (!parsed || typeof parsed !== "object") return null;
  for (const [name, def] of Object.entries(parsed)) {
    if ((def?.type || "").toLowerCase() !== "choices") continue;
    const input = Array.isArray(def?.inputs) ? def.inputs[0] : null;
    if (!input?.value) continue;
    return {
      fromName: name,
      toName: Array.isArray(def.to_name) ? def.to_name[0] : def.to_name,
      options: def.labels ?? [],
      dataKey: String(input.value).replace(/^\$/, ""),
      dataType: String(input.type || "text").toLowerCase(),
    };
  }
  return null;
};

const TaskMedia = ({ control, data }) => {
  const value = data?.[control.dataKey];
  if (value == null) return <span className={block.elem("media-missing").toClassName()}>—</span>;
  if (control.dataType === "image") {
    return <img className={block.elem("media-image").toClassName()} src={value} alt="" loading="lazy" />;
  }
  if (control.dataType === "audio" || control.dataType === "audioplus") {
    // biome-ignore lint/a11y/useMediaCaption: user-supplied media has no caption track
    return <audio className={block.elem("media-audio").toClassName()} src={value} controls preload="none" />;
  }
  return <p className={block.elem("media-text").toClassName()}>{String(value)}</p>;
};

const STATUS_LABELS = {
  saving: "Saving…",
  done: "Labelled",
  skipped: "Skipped",
  error: "Failed, click a choice to retry",
};

const Inner = () => {
  const { t } = useTranslation();
  const api = useAPI();
  const { project } = useContext(ProjectContext);
  const [tasks, setTasks] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // taskId -> { state: 'saving'|'done'|'skipped'|'error', annotationId, choice }
  const [status, setStatus] = useState({});
  const [focused, setFocused] = useState(0);
  const listRef = useRef(null);

  useUpdatePageTitle(createTitleFromSegments([project?.title, "Bulk label"]));

  const control = resolveChoicesControl(project);
  const enabled = !!project?.bulk_mode_enabled && !!control;

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    const res = await api.callApi("projectBulkTasks", {
      params: { pk: project.id },
      suppressError: true,
    });
    setLoading(false);
    if (res && Array.isArray(res.tasks)) {
      setTasks(res.tasks);
      setTotal(res.total ?? res.tasks.length);
      setStatus({});
      setFocused(0);
    }
  }, [api, project?.id]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const setTaskStatus = (taskId, patch) => setStatus((prev) => ({ ...prev, [taskId]: { ...prev[taskId], ...patch } }));

  const advanceFrom = useCallback(
    (index) => {
      setFocused((prev) => {
        const next = Math.min((tasks?.length ?? 1) - 1, Math.max(prev, index) + 1);
        return next;
      });
    },
    [tasks?.length],
  );

  const submit = useCallback(
    async (task, index, choice, { skip = false } = {}) => {
      const current = status[task.id];
      if (current?.state === "saving") return;
      // Re-clicking the already-saved choice is a no-op.
      if (!skip && current?.state === "done" && current?.choice === choice) return;

      const result = skip
        ? []
        : [
            {
              from_name: control.fromName,
              to_name: control.toName,
              type: "choices",
              value: { choices: [choice] },
            },
          ];
      setTaskStatus(task.id, { state: "saving", choice: skip ? undefined : choice });

      let res;
      if (current?.annotationId) {
        res = await api.callApi("updateTaskAnnotation", {
          params: { pk: current.annotationId },
          body: { result, was_cancelled: skip },
          suppressError: true,
        });
      } else {
        res = await api.callApi("submitTaskAnnotation", {
          params: { taskID: task.id },
          body: { result, was_cancelled: skip },
          suppressError: true,
        });
      }

      if (res && res.id && !res.error) {
        setTaskStatus(task.id, {
          state: skip ? "skipped" : "done",
          annotationId: res.id,
          choice: skip ? undefined : choice,
        });
        advanceFrom(index);
      } else {
        setTaskStatus(task.id, { state: "error" });
      }
    },
    [api, control, status, advanceFrom],
  );

  // Hotkeys: 1–9 apply the Nth choice to the focused task, ↑/↓ move
  // focus, S skips. Ignore keystrokes aimed at inputs/audio controls.
  useEffect(() => {
    if (!enabled || !tasks?.length) return undefined;
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      const task = tasks[focused];
      if (!task) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocused((f) => Math.min(tasks.length - 1, f + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocused((f) => Math.max(0, f - 1));
      } else if (e.key.toLowerCase() === "s") {
        e.preventDefault();
        submit(task, focused, null, { skip: true });
      } else if (/^[1-9]$/.test(e.key)) {
        const choice = control.options[Number(e.key) - 1];
        if (choice != null) {
          e.preventDefault();
          submit(task, focused, choice);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, tasks, focused, control, submit]);

  // Keep the focused row visible as focus auto-advances.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-bulk-index="${focused}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focused]);

  if (!project?.id) return null;

  const doneCount = Object.values(status).filter((s) => s.state === "done" || s.state === "skipped").length;
  const pageExhausted = tasks && tasks.length > 0 && doneCount >= tasks.length;
  const remaining = Math.max(0, total - doneCount);

  return (
    <ProjectLocaleScope projectId={project.id}>
      {/* Rate-change protection: blocking accept/exit modal. */}
      <RateChangeGate projectId={project.id} />
      <div className={`${block.toClassName()} ${cn("settings-page").mod({ wide: true }).toClassName()}`}>
        <TabHeader
          title={project?.title ? `${project.title}. Bulk label` : "Bulk label"}
          subtitle={t(
            "pages.bulk_label.subtitle",
            undefined,
            "Click a choice to label a task instantly. Keys 1–9 label the focused task, S skips, arrows move.",
          )}
          actions={
            /* Remaining-count chip on the title row — same stat-chip
               grammar as the org Quality page. */
            enabled ? (
              <div className={block.elem("remaining").toClassName()} aria-live="polite">
                <strong>{loading ? "…" : remaining}</strong>
                <span>{t("pages.bulk_label.remaining_for_you", undefined, "remaining for you")}</span>
              </div>
            ) : undefined
          }
        />

        {!enabled && (
          <SettingsCard
            title={t("pages.bulk_label.unavailable_title", undefined, "Bulk mode is off")}
            description={t(
              "pages.bulk_label.unavailable_description",
              undefined,
              "Enable it under Settings → Annotation → Bulk mode (requires a single-Choices labeling config).",
            )}
          />
        )}

        {enabled && (
          /* No card title (owner convention: the page header names the
             surface; the count lives on the title row). */
          <SettingsCard>
            {tasks && tasks.length === 0 && !loading && (
              <EmptyState
                title={t("pages.bulk_label.all_done_title", undefined, "Nothing left to label")}
                description={t(
                  "pages.bulk_label.all_done_description",
                  undefined,
                  "You're all caught up on this project.",
                )}
              />
            )}

            <div className={block.elem("list").toClassName()} ref={listRef}>
              {(tasks ?? []).map((task, index) => {
                const st = status[task.id];
                return (
                  <div
                    key={task.id}
                    data-bulk-index={index}
                    className={`${block.elem("row").toClassName()}${index === focused ? ` ${block.elem("row").mod({ focused: true }).toClassName()}` : ""}`}
                    onClick={() => setFocused(index)}
                  >
                    <div className={block.elem("media").toClassName()}>
                      <TaskMedia control={control} data={task.data} />
                    </div>
                    <div className={block.elem("choices").toClassName()}>
                      {control.options.map((choice, ci) => {
                        const active = st?.state === "done" && st?.choice === choice;
                        return (
                          <button
                            key={choice}
                            type="button"
                            className={`${block.elem("choice").toClassName()}${active ? ` ${block.elem("choice").mod({ active: true }).toClassName()}` : ""}`}
                            disabled={st?.state === "saving"}
                            onClick={(e) => {
                              e.stopPropagation();
                              submit(task, index, choice);
                            }}
                            title={ci < 9 ? `${choice} (${ci + 1})` : choice}
                          >
                            {choice}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        className={`${block.elem("choice").toClassName()} ${block.elem("choice").mod({ skip: true }).toClassName()}`}
                        disabled={st?.state === "saving"}
                        onClick={(e) => {
                          e.stopPropagation();
                          submit(task, index, null, { skip: true });
                        }}
                        title="Skip (S)"
                      >
                        {t("pages.bulk_label.skip", undefined, "Skip")}
                      </button>
                    </div>
                    <div
                      className={`${block.elem("status").toClassName()}${
                        st?.state
                          ? ` ${block
                              .elem("status")
                              .mod({ [st.state]: true })
                              .toClassName()}`
                          : ""
                      }`}
                    >
                      {st?.state ? STATUS_LABELS[st.state] : ""}
                    </div>
                  </div>
                );
              })}
            </div>

            {pageExhausted && remaining > 0 && (
              <div className={block.elem("footer").toClassName()}>
                <Button type="button" variant="primary" onClick={load} disabled={loading}>
                  {t("pages.bulk_label.load_next", undefined, "Load next batch")}
                </Button>
              </div>
            )}
          </SettingsCard>
        )}
      </div>
    </ProjectLocaleScope>
  );
};

export const BulkLabelPage = () => {
  const { id } = useParams();
  return (
    <ProjectProvider>
      <ConsentGate projectId={id}>
        <Inner />
      </ConsentGate>
    </ProjectProvider>
  );
};

BulkLabelPage.title = "Bulk label";
BulkLabelPage.path = "/bulk";
BulkLabelPage.exact = true;

BulkLabelPage.context = () => {
  const { project } = useContext(ProjectContext);
  const { user } = useAuth();
  if (!project?.id) return null;
  const role = user?.active_organization_role;
  const canManage =
    !!user &&
    (user.is_staff || user.is_superuser || ["OW", "AD", "MA"].includes(role) || project?.created_by?.id === user.id);
  return <ProjectNav projectId={project.id} canManage={canManage} project={project} userRole={role} />;
};
